import { useCallback, useMemo, useRef } from 'react';

import { usePlayerStore } from '../components/PlayerContextProvider';
import { usePlayerPause } from './usePlayerPause';
import { usePlayerPlay } from './usePlayerPlay';

export const usePlayerSeekChange = () => {
  const previousPlayingRef = useRef(false);
  const videoElement = usePlayerStore((state) => state.videoElement);
  const playing = usePlayerStore((state) => state.playing);
  const setSeeking = usePlayerStore((state) => state.setSeeking);
  const setCurrentTime = usePlayerStore((state) => state.setCurrentTime);
  const { onPlayerPlay } = usePlayerPlay();
  const { onPlayerPause } = usePlayerPause();

  const onPlayerSeekChange = useCallback(
    (value: number) => {
      if (!videoElement) return;

      videoElement.currentTime = value;
      setCurrentTime(value);
    },
    [videoElement, setCurrentTime],
  );

  const onPlayerSeekEnd = useCallback(() => {
    setSeeking(false);

    if (previousPlayingRef.current && videoElement) {
      onPlayerPlay();
      setCurrentTime(videoElement.currentTime);
    }
  }, [setSeeking, videoElement, setCurrentTime, onPlayerPlay]);

  const onPlayerSeekStart = useCallback(() => {
    setSeeking(true);

    previousPlayingRef.current = playing;

    if (videoElement && !videoElement.paused) {
      onPlayerPause();
      setCurrentTime(videoElement.currentTime);
    }
  }, [setSeeking, videoElement, playing, setCurrentTime, onPlayerPause]);

  return useMemo(
    () => ({
      onPlayerSeekChange,
      onPlayerSeekEnd,
      onPlayerSeekStart,
    }),
    [onPlayerSeekChange, onPlayerSeekEnd, onPlayerSeekStart],
  );
};
